import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  IonChip,
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonModal,
  IonPage,
  IonSpinner,
  createGesture,
  type Gesture,
  type InfiniteScrollCustomEvent,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Filter, Plus, X, MapPinned as MapIcon, ChefHat, Users } from 'lucide-react';
import { TopBar } from '../components/ui/TopBar';
import { PullToRefresh, type PullToRefreshHandle } from '../components/ui/PullToRefresh';
import { DishInfoCard } from '../components/listings/DishInfoCard';
import { EmptyHomeFeed } from '../components/listings/EmptyHomeFeed';
import { HomeFeedMapView } from '../components/listings/HomeFeedMapView';
import { MapErrorBoundary } from '../components/listings/MapErrorBoundary';
import { MapFailureOverlay } from '../components/listings/MapFailureOverlay';
import { resolveFailureReason, type MapStatus } from '../utils/mapDiagnostics';
import { ShareAppCard } from '../components/listings/ShareAppCard';
import { DietaryPromptCard } from '../components/listings/DietaryPromptCard';
import { HomeFeedRestrictionsPill } from '../components/listings/HomeFeedRestrictionsPill';
import { ActiveListingsOrdersPanel } from '../components/listings/ActiveListingsOrdersPanel';
import { ActiveOrdersPanel } from '../components/orders/ActiveOrdersPanel';
import { InlineLocationPrompt } from '../components/location/InlineLocationPrompt';
import { DistanceLocationGateModal } from '../components/location/DistanceLocationGateModal';
import {
  HomeFeedFilterModal,
  HomeFeedFilterState,
  EMPTY_FILTER_STATE,
  TimeRangePreset,
} from '../components/listings/HomeFeedFilterModal';
import { HomeFeedSortPill } from '../components/listings/HomeFeedSortPill';
import { useLocalListings, useLocalListingsForMap } from '../hooks/useListings';
import { useTagsRegistry } from '../hooks/useTagsRegistry';
import type { TagsRegistry } from '../services/tagsService';
import { useLocationStore, isSufficientLocation, hasLocale } from '../store/useLocationStore';
import { useBrowseAnchor } from '../hooks/useBrowseAnchor';
import { useAuthStore } from '../store/useAuthStore';
import { useProfileStore } from '../store/useProfileStore';
import { useDisplayProfile } from '../hooks/useDisplayProfile';
import { useHomeViewStore } from '../store/useHomeViewStore';
import { isChef, hasDietaryRestrictions } from '../types/profile';
import type { DishListing } from '../types/listing';
import { ChefApplicationModal } from '../components/onboarding/ChefApplicationModal';
import { SignInModal } from '../components/auth/SignInModal';
import { ConnectFriendsModal } from '../components/profile/ConnectFriendsModal';

// Rotating CTA strings on the chef "publish a dish" button. Keys live in
// `home.json`; the array order is just the cycle order (a random index
// seeds the first paint so refreshes don't always start the same way).
const PUBLISH_CTA_KEYS = [
  'feed.publishCta.1',
  'feed.publishCta.2',
  'feed.publishCta.3',
  'feed.publishCta.4',
];
const CTA_INTERVAL_MS = 5000;

// Connect-with-Friends feature flag. Flip to `true` to re-enable. The
// underlying ConnectFriendsModal + connection endpoints stay wired; this
// just hides the user-facing CTA on the empty-feed state. The feature
// needs more testing/polish before going live. See also ProfilePage.
const SHOW_CONNECTIONS_FEATURE = true;

/** Compute startDateTime / endDateTime ISO strings for a time-range preset. */
function getTimeRange(preset: TimeRangePreset): { startDateTime?: string; endDateTime?: string } {
  const now = new Date();
  const endOfDay = (d: Date) => {
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  if (preset === 'all') {
    return { startDateTime: now.toISOString() };
  }

  let end: Date;
  switch (preset) {
    case 'tomorrow': {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      end = endOfDay(tomorrow);
      break;
    }
    case 'thisWeek': {
      const d = new Date(now);
      const dayOfWeek = d.getDay();
      const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
      d.setDate(d.getDate() + daysUntilSunday);
      end = endOfDay(d);
      break;
    }
    case 'today':
    default:
      end = endOfDay(now);
  }

  return { startDateTime: now.toISOString(), endDateTime: end.toISOString() };
}

/** Flatten active filters into label + removal key pairs for the chip bar.
 *
 * `t` is bound to the `home` namespace (caller's `useTranslation('home')`).
 * Cross-namespace lookups use the `filter:key` prefix syntax. Tag VALUE
 * labels resolve through the BE registry (`tags` arg) — the registry's
 * `label` is the lang-specific display; the `value` (kebab-case key) is
 * what stays on the filter API param + the chip's removal key.
 *
 * Tag value not found in registry (e.g. cache hadn't loaded yet, or a
 * stale filter for a tag that's been removed from the registry) →
 * falls back to the raw value so the chip still renders something. */
function getActiveFilterChips(state: HomeFeedFilterState, t: TFunction, tags?: TagsRegistry) {
  const chips: { key: string; label: string }[] = [];

  const labelFor = (cat: 'dietary' | 'allergens' | 'origins', value: string): string => {
    const entry = tags?.[cat].find((e) => e.value === value);
    return entry?.label ?? value;
  };

  for (const tag of state.filters.dietaryInclude ?? []) {
    chips.push({ key: `di:${tag}`, label: labelFor('dietary', tag) });
  }
  for (const tag of state.filters.allergenExclude ?? []) {
    chips.push({ key: `ae:${tag}`, label: t('filter:chips.noTag', { tag: labelFor('allergens', tag) }) });
  }
  for (const tag of state.filters.originInclude ?? []) {
    chips.push({ key: `oi:${tag}`, label: labelFor('origins', tag) });
  }
  for (const level of state.filters.spicy ?? []) {
    chips.push({ key: `sp:${level}`, label: level === 0 ? t('filter:spice.notSpicy') : '🌶️'.repeat(level) });
  }
  if (state.filters.pickupEligible === true) {
    chips.push({ key: 'pick', label: t('filter:fulfillment.pickup') });
  }
  if (state.filters.hideSoldOut === true) {
    chips.push({ key: 'sold', label: t('filter:hideSoldOut') });
  }
  // Sort state is not chipped here — the always-visible HomeFeedSortPill
  // is the canonical surface for the current sort + direction.
  if (state.timeRange !== EMPTY_FILTER_STATE.timeRange) {
    chips.push({ key: 'time', label: t(`filter:window.${state.timeRange}`) });
  }
  return chips;
}


/** Merge two tag lists, dropping duplicates. `undefined` when the result is
 *  empty so the filter param stays absent from the request body. */
function unionTags(a: string[] | undefined, b: string[]): string[] | undefined {
  const merged = [...new Set([...(a ?? []), ...b])];
  return merged.length > 0 ? merged : undefined;
}

/** Remove a single chip from the filter state by its key. */
function removeChip(state: HomeFeedFilterState, chipKey: string): HomeFeedFilterState {
  const f = { ...state.filters };
  let { timeRange } = state;

  if (chipKey === 'pick') {
    delete f.pickupEligible;
  } else if (chipKey === 'sold') {
    delete f.hideSoldOut;
  } else if (chipKey === 'time') {
    timeRange = EMPTY_FILTER_STATE.timeRange;
  } else {
    const [prefix, value] = [chipKey.slice(0, 3), chipKey.slice(3)];
    if (prefix === 'di:') f.dietaryInclude = f.dietaryInclude?.filter((t) => t !== value);
    if (prefix === 'ae:') f.allergenExclude = f.allergenExclude?.filter((t) => t !== value);
    if (prefix === 'oi:') f.originInclude = f.originInclude?.filter((t) => t !== value);
    if (prefix === 'sp:') f.spicy = f.spicy?.filter((l) => l !== Number(value));
  }
  return { ...state, filters: f, timeRange };
}

export const HomePage: React.FC = () => {
  // `home` is the primary namespace for this page; `filter:` strings are
  // reached via the cross-namespace prefix in the chip-strip helper. The
  // tags registry feeds chip-strip labels for dietary/allergen/origin
  // filter values.
  const { t } = useTranslation(['home', 'connections']);
  const { data: tagsRegistry } = useTagsRegistry();
  const history = useHistory();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Optimistic-paint snapshot — falls back to localStorage-cached subset
  // when whoami's full profile fetch hasn't returned yet. Used for the
  // chef-vs-consumer branches below so a returning chef user doesn't see
  // the "Become a chef" prompt flash before whoami populates the live
  // profile. See `useDisplayProfile`.
  const displayProfile = useDisplayProfile();
  const localeId = useLocationStore((s) => s.localeId);
  const locale = useLocationStore((s) => s.locale);
  const location = useLocationStore((s) => s.location);
  const locationSource = useLocationStore((s) => s.locationSource);
  // Cached belief that the user isn't in a dish locale. Drives the
  // speculative empty-state below: when this is true we paint the
  // not-in-a-dish-locale screen instantly (no spinner) and let the
  // listings fetch run quietly in the background. If the fetch comes
  // back with listings (locale was promoted to dish since last cache),
  // the listings branch wins and the screen flips.
  const isPostalOnly = !!locale && locale.type !== 'dish';
  const mapOpen = useHomeViewStore((s) => s.mapOpen);
  const setMapOpen = useHomeViewStore((s) => s.setMapOpen);
  const mapBreakpoint = useHomeViewStore((s) => s.mapBreakpoint);
  const setMapBreakpoint = useHomeViewStore((s) => s.setMapBreakpoint);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterState, setFilterState] = useState<HomeFeedFilterState>(EMPTY_FILTER_STATE);
  // The saved-restrictions filter defaults ON and re-arms on every arrival at
  // the feed. This page is routed through a plain react-router <Switch> (not an
  // IonRouterOutlet), so navigating away unmounts it and coming back remounts —
  // which makes this `useState` initializer the re-arm. If the feed is ever
  // moved into a router outlet that keeps it mounted, this needs an explicit
  // `useIonViewWillEnter(() => setRestrictionsOn(true))` to keep the behavior.
  const [restrictionsOn, setRestrictionsOn] = useState(true);
  const [chefModalOpen, setChefModalOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  // Latest auth-intent the user expressed (Sign In vs Sign Up). Passed to
  // SignInModal as `initialMode` so the modal header reads the right verb
  // immediately, before the /exists call determines whether the contact
  // routes to login or register. Every site that opens the modal sets this
  // first — share-card popovers pass 'signUp', the share-empty-feed CTA
  // passes 'signUp', everything else defaults to 'signIn'.
  const [signInMode, setSignInMode] = useState<'signIn' | 'signUp'>('signIn');
  // Contextual line shown above the modal form, explaining why we're asking the
  // visitor to authenticate (e.g. connecting with a friend needs a "from" name).
  // Cleared unless the opening site provides one.
  const [signInIntro, setSignInIntro] = useState<string | undefined>(undefined);
  const openSignIn = (mode: 'signIn' | 'signUp', intro?: string) => {
    setSignInMode(mode);
    setSignInIntro(intro);
    setSignInOpen(true);
  };
  const [showConnectModal, setShowConnectModal] = useState(false);
  // Set when a logged-out visitor taps the "Connect with Friends" card CTA: we
  // send them through sign-up first, then reopen the connect modal once auth
  // flips so they land back where they intended.
  const connectAfterAuthRef = useRef(false);
  // Resume after an in-place sign-up: when auth flips true and the user got here
  // by tapping the connect card, close the sign-in modal and open the connect
  // modal so they continue the flow they started.
  useEffect(() => {
    if (!connectAfterAuthRef.current || !isAuthenticated) return;
    connectAfterAuthRef.current = false;
    setSignInOpen(false);
    setShowConnectModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  // Opened from a dish card's "no location" pin — explains why distance is
  // hidden and offers to enable device geolocation.
  const [distanceGateOpen, setDistanceGateOpen] = useState(false);
  // A map pick is always a list — single-listing taps just produce a 1-element
  // list. One state, one rendering path.
  const [mapSelection, setMapSelection] = useState<DishListing[] | null>(null);
  const showChefPrompt = isAuthenticated && !isChef(displayProfile);

  // The reference point the BE anchors the feed (results + distance sort) on.
  // Shared with the listing-detail query via useBrowseAnchor so both surfaces
  // measure distance from the same coords — see that hook for the resolution
  // rule and for why this is not the same thing as permission to show a
  // distance (that stays gated on locationSource === 'device').
  const anchor = useBrowseAnchor();
  // The anchor may be a locale centroid; this is the separate question of
  // whether it's actually the user's own position (see useBrowseAnchor).
  const hasPreciseLocation = useLocationStore((s) => s.locationSource === 'device');

  const params = useMemo(
    () =>
      localeId
        ? {
            localeId,
            lat: anchor?.lat,
            lon: anchor?.lon,
            // Same gate the UI uses to decide whether a distance may be shown,
            // so the feed never sinks a listing on a centroid measurement.
            preciseLocation: hasPreciseLocation,
            orderBy: filterState.orderBy,
            order: filterState.order,
          }
        : null,
    [localeId, anchor, hasPreciseLocation, filterState.orderBy, filterState.order],
  );

  // Wait for auth to reach a known terminal state before firing the listings
  // query. `/listings/local/search` is OptionalJWT — without it the BE
  // evaluates anonymously and CL-gated dishes are invisible, which is
  // indistinguishable from "empty locale." Two settled states:
  //   - `!isAuthenticated`: no csrf_token sentinel → definitively anon →
  //     fire immediately (BE returns whatever public listings exist).
  //   - `activeProfile != null`: whoami completed → access_token refreshed
  //     via the apiService chain → BE sees the authed identity.
  // `activeProfile` is not persisted (initial = null), so this gate naturally
  // re-arms on every cold start and the param-dep useMemo refires the query
  // once the post-whoami `setActiveProfile` lands.
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const authSettled = !isAuthenticated || activeProfile != null;
  const effectiveParams = authSettled ? params : null;

  // The user's saved dietary restrictions, folded into the feed query on top of
  // whatever they picked in the filter sheet. Union, not replace: an ad-hoc
  // dietary chip narrows the feed further rather than overriding the profile
  // preference. Read off `activeProfile` (the live profile) — the dietary
  // fields aren't carried on the lighter display snapshot.
  const savedDietaryTags = useMemo(
    () => activeProfile?.dietaryTags ?? [],
    [activeProfile?.dietaryTags],
  );
  const savedAllergenTags = useMemo(
    () => activeProfile?.allergenTags ?? [],
    [activeProfile?.allergenTags],
  );
  // "Answered with none" arms nothing and shows no pill — only real
  // restrictions do.
  const restrictionsActive = restrictionsOn && hasDietaryRestrictions(activeProfile);

  const filtersWithTime = useMemo(() => {
    const timeRange = getTimeRange(filterState.timeRange);
    const base = { ...filterState.filters, ...timeRange };
    if (!restrictionsActive) return base;
    return {
      ...base,
      dietaryInclude: unionTags(base.dietaryInclude, savedDietaryTags),
      allergenExclude: unionTags(base.allergenExclude, savedAllergenTags),
    };
  }, [
    filterState.filters,
    filterState.timeRange,
    restrictionsActive,
    savedDietaryTags,
    savedAllergenTags,
  ]);

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLocalListings(effectiveParams, filtersWithTime);
  // TanStack's refetch() ignores `enabled`. While the feed is gated (auth not
  // settled / no locale), a pull-to-refresh or logo tap would run the query
  // with null params and send `locale=undefined` → BE 500 → error modal.
  const handleRefresh = useCallback(
    () => (effectiveParams ? refetch() : Promise.resolve()),
    [effectiveParams, refetch],
  );
  // Dedupe across pages by id. Sort fields without a unique tiebreaker
  // (e.g. two listings sharing the same `windowStart`) can return the
  // same row at the page boundary, which surfaces as a React duplicate
  // key warning and a doubled card. Keep first occurrence so list
  // ordering matches what the server returned.
  const listings = useMemo(() => {
    if (!data) return undefined;
    const seen = new Set<string>();
    const out: DishListing[] = [];
    for (const page of data.pages) {
      for (const l of page.listings) {
        if (seen.has(l.id)) continue;
        seen.add(l.id);
        out.push(l);
      }
    }
    return out;
  }, [data]);

  // Map data is a separate query — fetched lazily on first open and cached
  // by TanStack Query keyed on the same params/filters as the list. The
  // BE caps each request at 100 rows, so the hook walks pages internally
  // to cover locales with more than 100 listings.
  const {
    data: mapListings,
    isLoading: mapLoading,
    isError: mapDataError,
    isFetching: mapFetching,
    refetch: refetchMapListings,
  } = useLocalListingsForMap(effectiveParams, filtersWithTime, mapOpen);

  // Map instance health, reported by HomeFeedMapView / MapErrorBoundary.
  // Bumping mapAttempt remounts both, which is how a map retry happens.
  const [mapStatus, setMapStatus] = useState<MapStatus>({ state: 'loading' });
  const [mapAttempt, setMapAttempt] = useState(0);
  // Only a failed fetch with nothing cached counts: stale listings on a
  // working map beat an error screen.
  const mapDataFailed = mapDataError && !mapListings && !mapFetching;
  const mapFailure = mapDataFailed
    ? resolveFailureReason('data')
    : mapStatus.state === 'failed'
      ? mapStatus.reason
      : null;
  const showMapSpinner =
    !mapFailure && (mapLoading || (mapFetching && !mapListings) || mapStatus.state === 'loading');

  const retryMap = () => {
    if (mapDataFailed) {
      void refetchMapListings();
      return;
    }
    setMapStatus({ state: 'loading' });
    setMapAttempt((n) => n + 1);
  };

  const chips = useMemo(
    () => getActiveFilterChips(filterState, t, tagsRegistry),
    [filterState, t, tagsRegistry],
  );

  // Human-readable rundown of what the restrictions pill is filtering on, e.g.
  // "Vegan, Gluten Free, No Peanuts". Labels resolve through the BE registry
  // (same source as the chip strip); an unknown value falls back to its raw
  // wire value so the summary never goes blank.
  const restrictionsSummary = useMemo(() => {
    const labelFor = (cat: 'dietary' | 'allergens', value: string) =>
      tagsRegistry?.[cat].find((e) => e.value === value)?.label ?? value;
    return [
      ...savedDietaryTags.map((v) => labelFor('dietary', v)),
      ...savedAllergenTags.map((v) =>
        t('filter:chips.noTag', { tag: labelFor('allergens', v) }),
      ),
    ].join(', ');
  }, [savedDietaryTags, savedAllergenTags, tagsRegistry, t]);
  const hasFilters = chips.length > 0;

  const userMapLocation =
    location?.lat != null && location?.lon != null ? { lat: location.lat, lon: location.lon } : null;

  const contentRef = useRef<HTMLIonContentElement>(null);
  const mapBlockerGestureRef = useRef<Gesture | null>(null);
  // Claims vertical gestures on the map area at a higher priority than the
  // modal-sheet gesture (~40), so Ionic's gesture controller deselects the
  // sheet drag when a touch starts on the map. Empty handlers — events still
  // reach Google Maps' own pan/zoom listeners.
  const setMapAreaRef = useCallback((el: HTMLDivElement | null) => {
    if (mapBlockerGestureRef.current) {
      mapBlockerGestureRef.current.destroy();
      mapBlockerGestureRef.current = null;
    }
    if (el) {
      mapBlockerGestureRef.current = createGesture({
        el,
        gestureName: 'home-map-pan-blocker',
        gesturePriority: 100,
        direction: 'y',
        threshold: 0,
        onMove: () => {},
      });
      mapBlockerGestureRef.current.enable(true);
    }
  }, []);

  const [showListPublishPeek, setShowListPublishPeek] = useState(false);
  const handleListScroll = (e: CustomEvent<{ scrollTop: number }>) => {
    setShowListPublishPeek((e.detail?.scrollTop ?? 0) > 70);
  };
  const scrollListToTop = () => {
    contentRef.current?.scrollToTop(300);
  };

  // Tapping the top-bar logo while already on the home feed can't navigate
  // anywhere new, so it "resets" the feed instead by running the exact same
  // refresh the pull gesture does (scroll to top + refetch + spinner). Both
  // funnel through the PullToRefresh component so the UI is defined once.
  const refresherRef = useRef<PullToRefreshHandle>(null);
  const handleHomeReset = useCallback(() => {
    refresherRef.current?.trigger();
  }, []);

  const filterButton = (
    <button
      type="button"
      onClick={() => setFilterOpen(true)}
      aria-label={t('filter:button')}
      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border ${
        hasFilters
          ? 'border-brand-primary bg-brand-primary/10 text-brand-primary'
          : 'border-border text-foreground'
      }`}
    >
      <Filter className="h-4 w-4" />
    </button>
  );

  const sortPill = (
    <HomeFeedSortPill
      orderBy={filterState.orderBy}
      order={filterState.order}
      onChange={({ orderBy, order }) =>
        setFilterState((prev) => ({ ...prev, orderBy, order }))
      }
    />
  );

  const chipsBar = hasFilters && (
    <div className="flex flex-nowrap gap-2 overflow-x-auto whitespace-nowrap [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
      {chips.map((chip) => (
        <IonChip
          key={chip.key}
          onClick={() => setFilterState((prev) => removeChip(prev, chip.key))}
          className="cursor-pointer flex-shrink-0"
        >
          {chip.label}
          <X className="ml-1 h-3.5 w-3.5 text-muted-foreground" />
        </IonChip>
      ))}
    </div>
  );

  const chefPromptCta = showChefPrompt && (
    // Mirror ShareAppCard's layout: a bordered card with heading + body text
    // that keeps the lion's share of the row, and a pill button that sits at
    // its content width on the right (dropping to its own full-width line once
    // large text makes it too wide to fit alongside the 60%-basis text).
    <div className="rounded-2xl border border-border bg-card px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2.5">
      <div className="min-w-0 grow-[999] basis-[60%]">
        <p className="text-sm font-medium text-foreground">{t('feed.chefPrompt.cardHeading')}</p>
        <p className="text-xs text-muted-foreground">{t('feed.chefPrompt.body')}</p>
      </div>
      <button
        type="button"
        onClick={() => setChefModalOpen(true)}
        className="flex grow basis-auto shrink-0 items-center justify-center gap-1.5 rounded-full bg-[var(--ion-color-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm active:scale-[0.99] transition-transform"
      >
        <ChefHat className="h-4 w-4" />
        {t('feed.chefPrompt.cta')}
      </button>
    </div>
  );

  // Same card shell as the chef prompt / ShareAppCard: a heading + explainer
  // that holds the lion's share of the row, with a pill button that drops to
  // its own line at large text. Shown on the empty feed to everyone so they can
  // seed their graph and start seeing friends' cooking. Signed-in users open the
  // connect modal directly; logged-out visitors are routed to sign-up first
  // since seeding a connection requires an account.
  const connectPromptCta = SHOW_CONNECTIONS_FEATURE && (
    <div className="rounded-2xl border border-border bg-card px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2.5">
      <div className="min-w-0 grow-[999] basis-[60%]">
        <p className="text-sm font-medium text-foreground">{t('connections:card.heading')}</p>
        <p className="text-xs text-muted-foreground">{t('connections:card.body')}</p>
      </div>
      <button
        type="button"
        onClick={() => {
          if (isAuthenticated) {
            setShowConnectModal(true);
            return;
          }
          connectAfterAuthRef.current = true;
          openSignIn('signUp', t('connections:card.signInIntro'));
        }}
        className="flex grow basis-auto shrink-0 items-center justify-center gap-1.5 rounded-full bg-[var(--ion-color-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm active:scale-[0.99] transition-transform"
      >
        <Users className="h-4 w-4" />
        {t('connections:card.cta')}
      </button>
    </div>
  );

  // Publish CTA shows for everyone with a location — unauthenticated visitors,
  // eaters, and chefs alike — and everyone enters the same publish flow. The
  // flow buffers the dish client-side for logged-out users and prompts account
  // creation at the final Publish step; eaters get gated into the chef quiz
  // there too. Chefs publish straight through.
  const handlePublishClick = () => {
    history.push('/publish-listing');
  };
  const [ctaIndex, setCtaIndex] = useState(() => Math.floor(Math.random() * PUBLISH_CTA_KEYS.length));
  // The CSS animation loops forever and is the ONLY clock: it fires
  // `animationiteration` at each loop boundary — the opacity-0 trough between
  // one message fading out and the next fading in — and we swap the text there,
  // invisibly. No setInterval racing the animation means no timer-vs-paint drift
  // (which is what made the very first swap glitch on a busy first paint), and a
  // dropped event can only stall the rotation on a still-visible string, never
  // blank it. Reduced-motion is handled in CSS (animation:none), so the event
  // never fires and the text holds — no JS guard needed.
  const advanceCta = useCallback(
    () => setCtaIndex((i) => (i + 1) % PUBLISH_CTA_KEYS.length),
    [],
  );

  const publishInline = (
    <button
      type="button"
      onClick={handlePublishClick}
      aria-label={t('feed.publishCta')}
      // Plus is anchored absolutely on the left so it stays put while the
      // rotating text changes width; the centered text only ever shifts
      // around the button's midpoint.
      className="relative flex w-full items-center justify-center rounded-2xl bg-[var(--ion-color-primary)] px-12 py-2.5 text-white font-semibold shadow-md active:scale-[0.99] transition-transform"
    >
      <Plus className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" />
      {/* Stack every rotating message in one grid cell. The invisible sizers
          reserve the height of the TALLEST message at the current width + text
          size, so the button holds that height for every message instead of
          shrinking and growing as the text rotates (some messages wrap to two
          lines at large text sizes). The visible message is overlaid in the
          same cell and keeps its fade/slide animation. */}
      <span className="grid grid-cols-1 w-full place-items-center text-center">
        {PUBLISH_CTA_KEYS.map((key) => (
          <span key={key} aria-hidden className="col-start-1 row-start-1 invisible">
            {t(key)}
          </span>
        ))}
        <span
          className="col-start-1 row-start-1 hp-cta-rotate"
          style={{ animationDuration: `${CTA_INTERVAL_MS}ms` }}
          onAnimationIteration={advanceCta}
        >
          {t(PUBLISH_CTA_KEYS[ctaIndex])}
        </span>
      </span>
    </button>
  );

  const errorStatus = (error as Error & { status?: number } | undefined)?.status;

  const hasListings = !!listings && listings.length > 0;

  // No sufficient location (country + postal) → the user landed on the feed
  // Two ways to be "ready for the feed":
  //   1. `hasLocale` — the location store already has a resolved locale.
  //      Either the user explicitly picked one this session (TopBar
  //      switcher / capture flow) or `hydrateLocationFromProfile` restored
  //      it from `profile.currentLocaleId` on auth. This is the
  //      authoritative signal.
  //   2. `hasLocation` — the user has a sufficient address (country +
  //      postalCode) but the locale hasn't resolved yet. Bridges the brief
  //      window between `setLocation` and `InitLocale` writing back the
  //      locale, so the prompt doesn't flicker in.
  // Either path gates the InlineLocationPrompt off and the feed renders.
  const localeReady = hasLocale(locale);
  const locationReady = isSufficientLocation(location);
  const hasLocation = localeReady || locationReady;

  return (
    <IonPage>
      {/* No-location is a full-screen location gate (welcome screen for anon,
          post-signup capture for authed) — hide the top bar in both cases. The
          gate's own corner slot renders Sign In (anon) or the profile avatar
          (authed) so the user still has a corner affordance. Once location
          resolves, the TopBar comes back here. */}
      {hasLocation && <TopBar showBack={false} onLogoReset={handleHomeReset} />}
      <IonContent
        ref={contentRef}
        scrollEvents
        onIonScroll={handleListScroll}
      >
        {!hasLocation ? (
          // No location yet → full-screen location gate (restored old look).
          <InlineLocationPrompt />
        ) : (
        <PullToRefresh
          ref={refresherRef}
          contentRef={contentRef}
          onRefresh={handleRefresh}
        >
        <div className="mx-auto max-w-lg px-4 pt-2 pb-28 space-y-4">
          {/* Chef-facing: live listings + their order-step tallies, pinned to
              the very top of the feed. Renders nothing for non-chefs / anyone
              without a qualifying listing. */}
          <ActiveListingsOrdersPanel />
          {/* Buyer-facing: the caller's own in-flight orders + live status
              breadcrumb, mirroring the chef panel above. Renders nothing for
              anyone without an in-flight order. */}
          <ActiveOrdersPanel />

          {/* Top-of-feed prompt for users who've never set their dietary
              restrictions. Self-hides once answered (including "none") and for
              logged-out visitors. */}
          <DietaryPromptCard />

          {hasFilters ? (
            <div className="sticky top-0 z-40 -mx-4 px-4 py-2 bg-card border-b border-border">
              <div className="flex items-center justify-between gap-1">
                <div className="flex-1 min-w-0">{chipsBar}</div>
                <div className="flex items-center flex-shrink-0">
                  {sortPill}
                  {filterButton}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2 first:pt-2">
              <h2 className="text-xl font-medium flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
                <img
                  src="/images/HomePlatedTM.png"
                  alt="HomePlated"
                  className="h-5 w-auto -translate-y-0.5 flex-shrink-0"
                />
                {/* "Dishes Nearby" sits beside the logo when it fits; when large
                    text would collide with the sort/filter controls, the whole
                    label drops to the next line, left-aligned under the logo.
                    `flex-wrap` + `whitespace-nowrap` keep it atomic so it moves
                    as one unit instead of breaking mid-phrase. */}
                <span className="whitespace-nowrap leading-tight">{t('feed.title')}</span>
              </h2>
              <div className="flex items-center flex-shrink-0">
                {sortPill}
                {filterButton}
              </div>
            </div>
          )}

          {/* Saved-restrictions toggle. Only for users who actually have
              restrictions saved; sits outside the chip strip because it's a
              profile preference, not an ad-hoc filter (see the component). */}
          {hasDietaryRestrictions(activeProfile) && (
            <HomeFeedRestrictionsPill
              active={restrictionsActive}
              onToggle={() => setRestrictionsOn((on) => !on)}
              summary={restrictionsSummary}
            />
          )}

          {publishInline}

          {/* Spinner is suppressed when we already believe the user isn't in a
              dish locale — the speculative empty-state below paints instantly
              and the fetch runs quietly. Listings (if they come back) win on
              the next render via the `hasListings` branch further down. */}
          {isLoading && !isPostalOnly && !hasListings && (
            <div className="flex justify-center py-12">
              <IonSpinner name="crescent" />
            </div>
          )}

          {/* Not-in-a-dish-locale empty state. Three sources, any wins:
              1. cached locale.type !== 'dish' AND no BE response yet
                 (speculative — the listings fetch could still come back
                 with a 200 if the locale was promoted to dish since the
                 cache was set; in that case `data` is populated even if
                 the array is empty, and this speculative branch retires
                 in favor of the listings render OR the "no-listings"
                 branch below);
              2. fetch came back 404 (confirmed not-a-dish-locale by BE);
              3. no localeId resolved yet. */}
          {!hasListings &&
            ((isPostalOnly && !data) ||
              errorStatus === 404 ||
              (!isLoading && !error && !localeId)) && (
              <EmptyHomeFeed reason="no-locale" />
            )}

          {!isLoading && !!error && errorStatus !== 404 && !hasListings && (
            <p className="text-center text-sm text-muted-foreground py-12">
              {t('feed.unableToLoad')}
            </p>
          )}

          {!isLoading && !error && localeId && listings?.length === 0 && (
            <>
              <EmptyHomeFeed
                reason="no-listings"
                action={
                  !isAuthenticated
                    ? { label: t('feed.empty.noListings.signUpCta'), onClick: () => openSignIn('signUp') }
                    : undefined
                }
              />
              {/* Signed-in users get the connect-with-friends affordance as a
                  card matching the chef prompt / share cards below, rather than
                  a lone button in the empty state. */}
              {connectPromptCta}
              {chefPromptCta}
              <ShareAppCard onRequestSignIn={openSignIn} />
            </>
          )}

          {hasListings && (
            <div data-testid="listings-list" className="space-y-4">
              {listings?.map((listing) => (
                <DishInfoCard
                  key={listing.id}
                  listing={listing}
                  onClick={() => history.push(`/listing/${listing.id}`)}
                  showDistanceGate
                  onDistanceGateClick={() => setDistanceGateOpen(true)}
                  showSoldOutStamp
                  hideOutOfRangeDropoff
                />
              ))}
            </div>
          )}

          {listings && listings.length > 0 && chefPromptCta}
          {listings && listings.length > 0 && <ShareAppCard onRequestSignIn={openSignIn} />}
        </div>

        <IonInfiniteScroll
          // Only gate on "no more pages." Don't add `isFetchingNextPage`
          // here: toggling `disabled` mid-scroll doesn't re-arm IonInfiniteScroll's
          // threshold, so the trigger stops firing while the user sits near
          // the bottom. Ionic's internal `complete()` lifecycle already
          // prevents concurrent fires; the in-handler guard is belt-and-braces.
          disabled={!hasNextPage}
          onIonInfinite={async (ev: InfiniteScrollCustomEvent) => {
            if (hasNextPage && !isFetchingNextPage) {
              await fetchNextPage();
            }
            ev.target.complete();
          }}
        >
          <IonInfiniteScrollContent loadingSpinner="crescent" />
        </IonInfiniteScroll>
        </PullToRefresh>
        )}
      </IonContent>

      {hasLocation && (
        <button
          type="button"
          onClick={scrollListToTop}
          aria-label={t('feed.map.publishBack')}
          style={{
            top: hasFilters
              ? 'calc(var(--ion-safe-area-top, 0px) + 128px)'
              : 'calc(var(--ion-safe-area-top, 0px) + 72px)',
          }}
          className={`fixed left-1/2 -translate-x-1/2 z-50 flex h-8 items-center justify-center rounded-full bg-[var(--ion-color-primary)] px-3 text-white shadow-lg transition-opacity ${
            showListPublishPeek ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <Plus className="h-5 w-5" />
        </button>
      )}

      {!mapOpen && hasListings && (
        <div
          className="fixed bottom-6 right-6 z-50"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <button
            type="button"
            onClick={() => setMapOpen(true)}
            aria-label={t('feed.map.show')}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--ion-color-primary)] text-white shadow-lg active:scale-95 transition-transform"
          >
            <MapIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      <IonModal
        isOpen={mapOpen && hasListings}
        // Cap modal at 80dvh; breakpoints are fractions of that height.
        // dvh tracks Chrome mobile's URL bar so the top of the sheet
        // (drag handle, close button) stays inside the visible viewport.
        style={{ '--height': '80dvh' } as React.CSSProperties}
        breakpoints={[0, 0.65, 1]}
        initialBreakpoint={mapBreakpoint}
        backdropBreakpoint={1}
        handle={false}
        onIonBreakpointDidChange={(e) => {
          const bp = e.detail.breakpoint;
          // Skip 0 — that's the dismissed state, not a height the user picked.
          if (bp > 0) setMapBreakpoint(bp);
        }}
        onDidDismiss={() => {
          setMapOpen(false);
          setMapSelection(null);
          // The map unmounts with the sheet; the next open starts fresh.
          setMapStatus({ state: 'loading' });
        }}
      >
        <div className="relative h-full w-full bg-card" style={{ isolation: 'isolate' }}>
          <div
            ref={setMapAreaRef}
            // Match the element to the visible portion so the map's center
            // (and its camera target) lines up with what the user sees.
            // Transition smooths the snap after a breakpoint settle.
            className="absolute inset-x-0 top-0 transition-[height] duration-300 ease-out"
            style={{ height: `calc(${mapBreakpoint * 80}dvh)` }}
          >
            <MapErrorBoundary
              key={mapAttempt}
              mapName="HomeFeedMap"
              onError={() => setMapStatus({ state: 'failed', reason: 'render' })}
            >
              <HomeFeedMapView
                className="absolute inset-0"
                listings={mapListings ?? []}
                userLocation={userMapLocation}
                onMarkerSelect={(listing) => setMapSelection([listing])}
                onClusterSelect={(clusterListings) => setMapSelection(clusterListings)}
                onMapTap={() => setMapSelection(null)}
                onStatusChange={setMapStatus}
              />
            </MapErrorBoundary>
            {mapFailure && (
              <MapFailureOverlay
                reason={mapFailure}
                onRetry={retryMap}
                onBackToList={() => setMapOpen(false)}
              />
            )}
            {showMapSpinner && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                <div className="rounded-full bg-card/90 px-3 py-2 shadow-md">
                  <IonSpinner name="crescent" />
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => setMapOpen(false)}
              aria-label={t('feed.map.close')}
              className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--ion-color-primary)] text-white shadow-lg active:scale-95 transition-transform"
              // Promote to own GPU layer; Chrome mobile otherwise composites
              // the Google Maps canvas above CSS-positioned siblings.
              style={{ transform: 'translateZ(0)' }}
            >
              <X className="h-5 w-5" />
            </button>
            {!mapFailure && mapSelection && mapSelection.length > 0 && (
              <div className="absolute left-0 right-0 bottom-3 z-10">
                <div
                  className="flex overflow-x-auto snap-x snap-mandatory gap-3 px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  onClick={(e) => {
                    // Taps on the strip's empty area (padding, gaps between
                    // cards, space beside a lone card) should dismiss like a
                    // map tap. Taps that land on a card descend past this
                    // handler — e.target won't match e.currentTarget.
                    if (e.target === e.currentTarget) setMapSelection(null);
                  }}
                >
                  {mapSelection.map((listing) => (
                    <div key={listing.id} className="flex-none w-[280px] snap-center">
                      <DishInfoCard
                        listing={listing}
                        onClick={() => history.push(`/listing/${listing.id}`)}
                        defaultExpanded={false}
                        variant="floating"
                        showDistanceGate
                        onDistanceGateClick={() => setDistanceGateOpen(true)}
                        showSoldOutStamp
                        hideOutOfRangeDropoff
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Drag handle: rendered as a sibling above the map (higher z),
              so touches start outside the map's gesture-blocker and the
              modal's sheet-drag gesture handles them. Frosted capsule keeps
              the grabber legible against any map background. */}
          <div
            className="absolute inset-x-0 top-0 z-30 flex items-start justify-center pt-2 select-none"
            // Promote to own GPU layer; Chrome mobile otherwise composites
            // the Google Maps canvas above CSS-positioned siblings.
            style={{ transform: 'translateZ(0)' }}
          >
            <div className="flex items-center justify-center rounded-full bg-[var(--ion-color-primary)] px-3 py-1.5 shadow-md">
              <div className="h-1 w-9 rounded-full bg-white" />
            </div>
          </div>
        </div>
      </IonModal>

      <HomeFeedFilterModal
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={filterState}
        onChange={setFilterState}
      />
      <ChefApplicationModal
        isOpen={chefModalOpen}
        onClose={() => setChefModalOpen(false)}
      />
      <SignInModal
        isOpen={signInOpen}
        onClose={() => setSignInOpen(false)}
        initialMode={signInMode}
        intro={signInIntro}
      />
      <DistanceLocationGateModal
        isOpen={distanceGateOpen}
        onClose={() => setDistanceGateOpen(false)}
      />
      <ConnectFriendsModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
      />
    </IonPage>
  );
};
